import { z } from "zod";

/**
 * Dermatology skin-map API contracts. Mirrors the dental module: a flat
 * list of lesions the UI renders as markers on a body silhouette.
 */

export const bodyViewSchema = z.enum([
	"anterior",
	"posterior",
	"left",
	"right",
	"face",
	"scalp",
	"hands",
]);
export type BodyView = z.infer<typeof bodyViewSchema>;

export const skinLesionTypeSchema = z.enum([
	"mole",
	"melanoma_suspect",
	"rash",
	"acne",
	"psoriasis",
	"eczema",
	"wart",
	"cyst",
	"scar",
	"burn",
	"vitiligo",
	"treatment_site",
	"other",
]);
export type SkinLesionType = z.infer<typeof skinLesionTypeSchema>;

export const skinLesionStatusSchema = z.enum([
	"active",
	"monitoring",
	"treated",
	"removed",
	"resolved",
]);
export type SkinLesionStatus = z.infer<typeof skinLesionStatusSchema>;

/** How widespread the lesion/eruption is — a structured (queryable) field. */
export const skinDistributionSchema = z.enum([
	"localized", // single site
	"regional", // one body region
	"generalized", // widespread / multiple regions
]);
export type SkinDistribution = z.infer<typeof skinDistributionSchema>;

/**
 * ABCDE melanoma screening flags — the dermatology gold standard for
 * triaging a pigmented lesion. Each is a clinician yes/no observation:
 *   A — Asymmetry
 *   B — Border irregularity
 *   C — Color variegation
 *   D — Diameter > 6 mm
 *   E — Evolving (changed since last visit)
 */
export const abcdeSchema = z.object({
	asymmetry: z.boolean().default(false),
	border: z.boolean().default(false),
	color: z.boolean().default(false),
	diameter: z.boolean().default(false),
	evolving: z.boolean().default(false),
});
export type Abcde = z.infer<typeof abcdeSchema>;

export type AbcdeRisk = "low" | "moderate" | "high";

/** Number of positive ABCDE criteria (0–5). */
export function abcdeScore(a: Abcde | null | undefined): number {
	if (!a) return 0;
	return [a.asymmetry, a.border, a.color, a.diameter, a.evolving].filter(
		Boolean,
	).length;
}

/**
 * Risk tier from the ABCDE count. Conservative on purpose: ANY positive
 * criterion lifts the lesion out of "low", and ≥3 (or evolving — the single
 * most predictive sign — plus one other) flags "high" → biopsy / referral
 * consideration.
 */
export function abcdeRisk(a: Abcde | null | undefined): AbcdeRisk {
	const s = abcdeScore(a);
	if (s >= 3 || (a?.evolving && s >= 2)) return "high";
	if (s >= 1) return "moderate";
	return "low";
}

export const skinLesionSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	body_view: bodyViewSchema,
	pos_x: z.number().int(),
	pos_y: z.number().int(),
	region: z.string().nullable(),
	lesion_type: skinLesionTypeSchema,
	status: skinLesionStatusSchema,
	size_mm: z.string().nullable(),
	// Structured symptom/appearance fields (queryable, beyond free-text notes).
	color: z.string().nullable(),
	distribution: skinDistributionSchema.nullable(),
	itch: z.boolean().nullable(),
	pain: z.boolean().nullable(),
	notes: z.string().nullable(),
	photo_id: z.string().uuid().nullable(),
	abcde: abcdeSchema.nullable(),
	/** Count of clinical photos linked to this lesion (computed). */
	photo_count: z.number().int().nonnegative().default(0),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type SkinLesionApi = z.infer<typeof skinLesionSchema>;

const coord = z.number().int().min(0).max(1000);

export const createSkinLesionInputSchema = z.object({
	patient_id: z.string().uuid(),
	body_view: bodyViewSchema.default("anterior"),
	pos_x: coord,
	pos_y: coord,
	region: z.string().max(200).optional(),
	lesion_type: skinLesionTypeSchema.default("mole"),
	status: skinLesionStatusSchema.default("active"),
	size_mm: z.coerce.number().nonnegative().max(9999).optional(),
	color: z.string().max(60).optional(),
	distribution: skinDistributionSchema.optional(),
	itch: z.boolean().optional(),
	pain: z.boolean().optional(),
	notes: z.string().max(1000).optional(),
	photo_id: z.string().uuid().nullable().optional(),
	abcde: abcdeSchema.optional(),
});
export type CreateSkinLesionInput = z.infer<typeof createSkinLesionInputSchema>;

export const updateSkinLesionInputSchema = z.object({
	region: z.string().max(200).nullable().optional(),
	lesion_type: skinLesionTypeSchema.optional(),
	status: skinLesionStatusSchema.optional(),
	size_mm: z.coerce.number().nonnegative().max(9999).nullable().optional(),
	color: z.string().max(60).nullable().optional(),
	distribution: skinDistributionSchema.nullable().optional(),
	itch: z.boolean().nullable().optional(),
	pain: z.boolean().nullable().optional(),
	notes: z.string().max(1000).nullable().optional(),
	photo_id: z.string().uuid().nullable().optional(),
	abcde: abcdeSchema.nullable().optional(),
	pos_x: coord.optional(),
	pos_y: coord.optional(),
});
export type UpdateSkinLesionInput = z.infer<typeof updateSkinLesionInputSchema>;

/**
 * Longitudinal monitoring log for a single lesion. Each row is a dated
 * snapshot (size + status + ABCDE + optional photo) so the dermatologist can
 * chart how a mole evolves over months — the core melanoma-surveillance
 * workflow. The parent lesion always mirrors the latest snapshot for fast
 * map rendering.
 */
export const skinLesionMeasurementSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	lesion_id: z.string().uuid(),
	size_mm: z.string().nullable(),
	status: skinLesionStatusSchema,
	abcde: abcdeSchema.nullable(),
	photo_id: z.string().uuid().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	taken_at: z.string().datetime(),
	created_at: z.string().datetime(),
});
export type SkinLesionMeasurementApi = z.infer<
	typeof skinLesionMeasurementSchema
>;

export const createSkinLesionMeasurementInputSchema = z.object({
	lesion_id: z.string().uuid(),
	size_mm: z.coerce.number().nonnegative().max(9999).optional(),
	status: skinLesionStatusSchema.optional(),
	abcde: abcdeSchema.optional(),
	photo_id: z.string().uuid().nullable().optional(),
	notes: z.string().max(1000).optional(),
	taken_at: z.string().datetime().optional(),
});
export type CreateSkinLesionMeasurementInput = z.infer<
	typeof createSkinLesionMeasurementInputSchema
>;

// ===== AI before/after lesion comparison (vision-assisted progress check) =====

/** Overall trajectory the AI reads between the two photos. */
export const lesionChangeDirectionSchema = z.enum([
	"improved",
	"worsened",
	"stable",
	"unclear",
]);
export type LesionChangeDirection = z.infer<typeof lesionChangeDirectionSchema>;

/** How much the change warrants clinician attention. */
export const lesionConcernLevelSchema = z.enum(["low", "moderate", "high"]);
export type LesionConcernLevel = z.infer<typeof lesionConcernLevelSchema>;

/** Which visual aspect a single observed change is about. */
export const lesionChangeAspectSchema = z.enum([
	"size",
	"color",
	"border",
	"texture",
	"elevation",
	"surrounding_skin",
	"other",
]);
export type LesionChangeAspect = z.infer<typeof lesionChangeAspectSchema>;

/** The validated model output for one comparison. */
export const lesionComparisonResultSchema = z.object({
	change_direction: lesionChangeDirectionSchema,
	concern_level: lesionConcernLevelSchema,
	/** 1–3 sentence plain-language read of the difference. */
	summary: z.string().default(""),
	/** Per-aspect observed changes (size grew, border more irregular, …). */
	changes: z
		.array(
			z.object({
				aspect: lesionChangeAspectSchema,
				observation: z.string(),
			}),
		)
		.default([]),
	/** AI-suggested ABCDE flags on the LATER photo — a DRAFT the clinician
	 *  confirms in the normal ABCDE fields (never auto-applied). */
	abcde: abcdeSchema.nullable().default(null),
	/** What the AI suggests the clinician do next (review / re-image / refer). */
	recommendation: z.string().default(""),
	/** Suggested follow-up interval, if any. */
	follow_up_days: z.number().int().positive().nullable().default(null),
});
export type LesionComparisonResult = z.infer<
	typeof lesionComparisonResultSchema
>;

/** Stored comparison row as returned to the client. */
export const lesionComparisonSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	lesion_id: z.string().uuid(),
	before_photo_id: z.string().uuid().nullable(),
	after_photo_id: z.string().uuid().nullable(),
	before_taken_at: z.string().datetime().nullable(),
	after_taken_at: z.string().datetime().nullable(),
	change_direction: lesionChangeDirectionSchema,
	concern_level: lesionConcernLevelSchema,
	summary: z.string(),
	result: lesionComparisonResultSchema,
	model: z.string(),
	created_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type LesionComparisonApi = z.infer<typeof lesionComparisonSchema>;

/** Request to compare two of a lesion's photos. */
export const compareLesionInputSchema = z.object({
	lesion_id: z.string().uuid(),
	before_photo_id: z.string().uuid(),
	after_photo_id: z.string().uuid(),
});
export type CompareLesionInput = z.infer<typeof compareLesionInputSchema>;
